import React, { useState } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    Alert,
    ScrollView,
    ActivityIndicator
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useCart } from '../../context/CartContext';
import { createOrder } from '../../api/marketplace';

const CheckoutScreen = ({ navigation }) => {
    const { cartItems, getCartTotal, clearCart } = useCart();
    const [loading, setLoading] = useState(false);
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('mpesa');

    const handleCheckout = async () => {
        if (!deliveryAddress || !contactPhone) {
            Alert.alert('Error', 'Please fill in delivery address and contact phone.');
            return;
        }

        try {
            setLoading(true);
            const orderData = {
                items: cartItems,
                delivery_address: { street: deliveryAddress }, // Simple object for now
                contact_phone: contactPhone,
                total_amount: getCartTotal(),
                payment_method: paymentMethod
            };

            await createOrder(orderData);

            clearCart();
            Alert.alert('Success', 'Order placed successfully! Please complete payment on your phone.', [
                { text: 'OK', onPress: () => navigation.navigate('OrderHistory') }
            ]);
        } catch (error) {
            console.error('Checkout error:', error);
            Alert.alert('Error', 'Checkout failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <ScrollView style={styles.container}>
            <View style={styles.header}>
                <TouchableOpacity onPress={() => navigation.goBack()}>
                    <Ionicons name="arrow-back" size={24} color="#111827" />
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Checkout</Text>
                <View style={{ width: 24 }} />
            </View>

            <View style={styles.content}>
                <View style={styles.sectionHeaderRow}>
                    <Text style={styles.sectionTitle}>Delivery Details</Text>
                    <TouchableOpacity style={styles.pinButton} onPress={() => {
                        Alert.alert("Location", "Detecting your current location...");
                        setTimeout(() => {
                            setDeliveryAddress("Kitengela, Main Street (Pinned)");
                        }, 1000);
                    }}>
                        <Ionicons name="location-outline" size={16} color="#6366F1" />
                        <Text style={styles.pinText}>PIN MY LOCATION</Text>
                    </TouchableOpacity>
                </View>

                <View style={styles.inputWrapper}>
                    <Ionicons name="map-outline" size={20} color="#9CA3AF" style={styles.inputIcon} />
                    <TextInput
                        style={styles.input}
                        placeholder="Street / Building / Area"
                        value={deliveryAddress}
                        onChangeText={setDeliveryAddress}
                    />
                </View>

                <View style={styles.inputWrapper}>
                    <Ionicons name="call-outline" size={20} color="#9CA3AF" style={styles.inputIcon} />
                    <TextInput
                        style={styles.input}
                        placeholder="Contact Phone (254...)"
                        keyboardType="phone-pad"
                        value={contactPhone}
                        onChangeText={setContactPhone}
                    />
                </View>

                <Text style={styles.sectionTitle}>Payment Method</Text>
                <View style={styles.paymentOption}>
                    <Ionicons name="phone-portrait-outline" size={24} color="#10B981" />
                    <Text style={styles.paymentText}>M-Pesa (STK Push)</Text>
                    <Ionicons name="checkmark-circle" size={24} color="#10B981" />
                </View>

                <View style={styles.summary}>
                    <Text style={styles.summaryTitle}>Order Summary</Text>
                    {cartItems.map(item => (
                        <View key={item.id} style={styles.summaryRow}>
                            <Text style={styles.summaryItem}>{item.name} x {item.quantity}</Text>
                            <Text style={styles.summaryPrice}>{item.price * item.quantity}</Text>
                        </View>
                    ))}
                    <View style={styles.divider} />
                    <View style={styles.totalRow}>
                        <Text style={styles.totalLabel}>Total to Pay</Text>
                        <Text style={styles.totalAmount}>KES {getCartTotal().toLocaleString()}</Text>
                    </View>
                </View>

                <TouchableOpacity
                    style={[styles.payButton, loading && styles.disabledButton]}
                    onPress={handleCheckout}
                    disabled={loading}
                >
                    {loading ? (
                        <ActivityIndicator color="#FFFFFF" />
                    ) : (
                        <Text style={styles.payButtonText}>Pay Now</Text>
                    )}
                </TouchableOpacity>
            </View>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F9FAFB',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 16,
        paddingTop: 60,
        backgroundColor: '#FFFFFF',
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: '600',
    },
    content: {
        padding: 20,
    },
    sectionHeaderRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
        marginTop: 20,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: '#111',
    },
    pinButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#EEF2FF',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 8,
        gap: 4,
    },
    pinText: {
        fontSize: 11,
        fontWeight: '800',
        color: '#6366F1',
    },
    inputWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#E5E7EB',
        borderRadius: 12,
        paddingHorizontal: 12,
        marginBottom: 12,
    },
    inputIcon: {
        marginRight: 10,
    },
    input: {
        flex: 1,
        paddingVertical: 14,
        fontSize: 15,
        color: '#111',
    },
    paymentOption: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#ECFDF5',
        padding: 16,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#10B981',
        justifyContent: 'space-between',
    },
    paymentText: {
        fontWeight: '600',
        color: '#065F46',
        flex: 1,
        marginLeft: 12,
    },
    summary: {
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        padding: 16,
        marginTop: 24,
    },
    summaryTitle: {
        fontWeight: '600',
        marginBottom: 12,
    },
    summaryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 8,
    },
    summaryItem: {
        color: '#6B7280',
    },
    divider: {
        height: 1,
        backgroundColor: '#E5E7EB',
        marginVertical: 12,
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    totalLabel: {
        fontWeight: '700',
        fontSize: 16,
    },
    totalAmount: {
        fontWeight: '700',
        fontSize: 18,
        color: '#10B981',
    },
    payButton: {
        backgroundColor: '#10B981',
        paddingVertical: 16,
        borderRadius: 12,
        alignItems: 'center',
        marginTop: 32,
        marginBottom: 40,
    },
    disabledButton: {
        opacity: 0.7,
    },
    payButtonText: {
        color: '#FFFFFF',
        fontSize: 18,
        fontWeight: '700',
    },
});

export default CheckoutScreen;
